import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { reserveNotification, releaseNotification, useChatIsOpen } from './notificationVisibility';

interface LiveConfig {
  position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  display_time: number;
  pause_after_close: number;
  is_active: boolean;
  show_on_mobile: boolean;
}

interface LiveType {
  type_name: string;
  min_interval: number;
  max_interval: number;
  is_active: boolean;
}

const DEFAULT_CONFIG: LiveConfig = {
  // Preserva o posicionamento original caso o Supabase esteja temporariamente indisponível.
  position: 'bottom-right',
  display_time: 5,
  pause_after_close: 5,
  is_active: true,
  show_on_mobile: true,
};

const positionClasses: Record<LiveConfig['position'], string> = {
  'bottom-left': 'bottom-[4px] left-1 sm:bottom-6 sm:left-6',
  'bottom-right': 'bottom-[4px] right-1 sm:bottom-6 sm:right-6',
  'top-left': 'top-24 left-1 sm:left-6',
  'top-right': 'top-24 right-1 sm:right-6',
};

const randomBetween = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const EXIT_ANIMATION_MS = 1000;
const START_AFTER_SCROLL_MS = 2000;

const ViewersNotification = () => {
  const chatOpen = useChatIsOpen();
  const [viewers, setViewers] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [progress, setProgress] = useState(100);
  const manualExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ownedRef = useRef(false);
  const closingManuallyRef = useRef(false);
  const [config, setConfig] = useState<LiveConfig>(DEFAULT_CONFIG);
  const [visitorType, setVisitorType] = useState<LiveType | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tela com largura inferior a 640px: mesma faixa usada pelo Tailwind antes de sm:.
  // Nenhuma classe CSS nova e nenhum bloqueio para tablets/computadores.
  const [isSmartphone, setIsSmartphone] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const syncDevice = () => setIsSmartphone(mediaQuery.matches);
    syncDevice();
    mediaQuery.addEventListener('change', syncDevice);
    return () => mediaQuery.removeEventListener('change', syncDevice);
  }, []);

  // Não exibir nada no carregamento: esperar a primeira rolagem + 2 segundos.
  useEffect(() => {
    let readyTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (window.scrollY < 10 || readyTimer) return;
      window.removeEventListener('scroll', onScroll);
      readyTimer = setTimeout(() => setHasScrolled(true), START_AFTER_SCROLL_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (readyTimer) clearTimeout(readyTimer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadSettings = async () => {
      try {
        // A falha dos tipos não pode impedir a leitura da posição/status.
        const configResult = await supabase
          .from('ibx_live_notifications_config').select('*').maybeSingle();
        if (configResult.error) throw configResult.error;
        if (!mounted) return;

        if (configResult.data) {
          const record = configResult.data;
          const candidate = String(record.position);
          const position = Object.prototype.hasOwnProperty.call(positionClasses, candidate)
            ? candidate as LiveConfig['position']
            : DEFAULT_CONFIG.position;
          setConfig({
            position,
            display_time: Number(record.display_time) || DEFAULT_CONFIG.display_time,
            pause_after_close: Number(record.pause_after_close) || DEFAULT_CONFIG.pause_after_close,
            is_active: record.is_active === true,
            show_on_mobile: record.show_on_mobile !== false,
          });
        } else {
          logger.error('Configuração AO VIVO não encontrada no Supabase; usando valores originais.', null);
        }
      } catch (error) {
        // Se o banco ficar indisponível, preserva o aviso original e registra o erro.
        logger.error('Falha ao carregar configurações AO VIVO; usando padrão original:', error);
      } finally {
        if (mounted) setSettingsLoaded(true);
      }
      try {
        const typesResult = await supabase.from('ibx_notification_types').select('*');
        if (typesResult.error) throw typesResult.error;
        if (!mounted) return;
        const visitor = (typesResult.data || []).find(t =>
          String(t.type_name).toLowerCase().includes('visitant')
        );
        setVisitorType(visitor || null);
      } catch (error) {
        logger.error('Erro ao carregar intervalo/tipo de visitantes AO VIVO:', error);
      }
    };
    void loadSettings();
    const reloadOnFocus = () => { void loadSettings(); };
    window.addEventListener('focus', reloadOnFocus);
    return () => { mounted = false; window.removeEventListener('focus', reloadOnFocus); };
  }, []);

  useEffect(() => {
    setViewers(randomBetween(8, 34));
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const update = () => {
      if (cancelled) return;
      setViewers(prev => Math.min(34, Math.max(8, prev + randomBetween(-3, 3))));
      timer = setTimeout(update, randomBetween(20000, 45000));
    };
    timer = setTimeout(update, randomBetween(20000, 45000));
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const active = settingsLoaded && config.is_active && (!visitorType || visitorType.is_active) &&
    (!isSmartphone || config.show_on_mobile);

  // Desligar o aviso imediatamente caso a configuração de smartphone o proíba.
  useEffect(() => {
    if (!active) {
      setIsVisible(false);
      setIsMounted(false);
      if (ownedRef.current) {
        releaseNotification('live');
        ownedRef.current = false;
      }
    }
  }, [active]);

  useEffect(() => {
    if (!active || !hasScrolled || isPaused || chatOpen) return;
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    let enterTimer: ReturnType<typeof setTimeout> | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let exitTimer: ReturnType<typeof setTimeout> | undefined;
    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;
    const displayMs = Math.min(15, Math.max(3, config.display_time)) * 1000;

    const show = () => {
      if (cancelled || closingManuallyRef.current) return;
      if (!reserveNotification('live')) {
        showTimer = setTimeout(show, 600);
        return;
      }
      ownedRef.current = true;
      setIsMounted(true);
      setIsVisible(false);
      setProgress(100);
      enterTimer = setTimeout(() => setIsVisible(true), 40);
      const startedAt = Date.now();
      progressInterval = setInterval(() => {
        setProgress(Math.max(0, 100 - ((Date.now() - startedAt) / displayMs) * 100));
      }, 100);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        if (progressInterval) clearInterval(progressInterval);
        exitTimer = setTimeout(() => {
          setIsMounted(false);
          releaseNotification('live');
          ownedRef.current = false;
          const min = visitorType ? Math.max(5, Number(visitorType.min_interval) || 10) : 15;
          const max = visitorType ? Math.max(min, Number(visitorType.max_interval) || min) : 25;
          showTimer = setTimeout(show, randomBetween(min, max) * 1000);
        }, EXIT_ANIMATION_MS);
      }, displayMs);
    };

    showTimer = setTimeout(show, 0);
    return () => {
      cancelled = true;
      if (showTimer) clearTimeout(showTimer);
      if (enterTimer) clearTimeout(enterTimer);
      if (hideTimer) clearTimeout(hideTimer);
      if (exitTimer) clearTimeout(exitTimer);
      if (progressInterval) clearInterval(progressInterval);
      if ((!closingManuallyRef.current || chatOpen) && ownedRef.current) {
        releaseNotification('live');
        ownedRef.current = false;
      }
    };
  }, [active, hasScrolled, config.display_time, visitorType?.min_interval, visitorType?.max_interval, isPaused, chatOpen]);

  useEffect(() => () => {
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    if (manualExitTimer.current) clearTimeout(manualExitTimer.current);
    releaseNotification('live');
  }, []);

  const handleClose = () => {
    if (closingManuallyRef.current) return;
    closingManuallyRef.current = true;
    setIsVisible(false);
    setIsPaused(true);
    if (manualExitTimer.current) clearTimeout(manualExitTimer.current);
    manualExitTimer.current = setTimeout(() => {
      setIsMounted(false);
      releaseNotification('live');
      ownedRef.current = false;
      closingManuallyRef.current = false;
    }, EXIT_ANIMATION_MS);
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    const pauseMs = Math.min(30, Math.max(1, config.pause_after_close)) * 60000;
    pauseTimer.current = setTimeout(() => setIsPaused(false), pauseMs);
  };

  if (!isMounted || !active || chatOpen) return null;

  return (
    <div
      className={`ix-live-motion fixed ${positionClasses[config.position]} z-40 w-[340px] max-w-[calc(100vw-8px)] sm:w-[320px] sm:max-w-[calc(100vw-3rem)]`}
      aria-hidden={!isVisible}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate3d(0, 0, 0)' : config.position.endsWith('left')
          ? 'translate3d(calc(-100% - 48px), 0, 0)'
          : 'translate3d(calc(100% + 48px), 0, 0)',
        transition: 'transform 1000ms cubic-bezier(0.22, 1, 0.36, 1), opacity 460ms ease',
      }}
    >
      <div className="w-full overflow-hidden rounded-t-xl rounded-b-none border border-gray-100 bg-white shadow-2xl">
        <div className="relative px-3 py-3 sm:px-4 sm:py-4">
          <button
            type="button"
            aria-label="Fechar aviso AO VIVO"
            onClick={handleClose}
            disabled={!isVisible}
            className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>

          <div className="flex min-w-0 items-start gap-2.5 sm:gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 sm:h-10 sm:w-10">
              <i className="ri-live-line text-xl text-red-500" aria-hidden="true"></i>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-1.5 pr-7">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden="true"></span>
                <span className="text-[11px] font-semibold tracking-wide text-gray-900">AO VIVO</span>
              </div>
              <p className="text-[14px] font-bold leading-snug text-gray-900">
                {viewers} pessoas estão vendo agora
              </p>
              <p className="mt-1 text-[11px] text-gray-500">Alta demanda neste momento</p>
            </div>
          </div>
        </div>
        <div className="h-[3px] w-full bg-gray-100" aria-hidden="true">
          <div
            className="h-full bg-red-500"
            style={{ width: `${progress}%`, transition: 'width 100ms linear' }}
          />
        </div>
      </div>
      <style>{`@media (prefers-reduced-motion: reduce) { .ix-live-motion { transition-duration: 0ms !important; } }`}</style>
    </div>
  );
};

export default ViewersNotification;
